import React, {useState} from 'react';
import {Card, Stack, useTheme} from '@mui/material';
import Typography from '@mui/material/Typography';
import {TruvuButton} from '../../../components/button/TruvuButton';
import {styled} from '@mui/material/styles';
import {useMutation} from 'react-relay-mutation';
import {SubscriptionCardMutation} from '../../../__generated__/SubscriptionCardMutation.graphql';
import graphql from 'babel-plugin-relay/macro';
import {useSnackbarContext} from '../../../context/SnackbarContext';
import {SubscriptionCardManageDialog} from './SubscriptionCardManageDialog';
import {useTruvuDialog} from '../../../components/dialog/TruvuDialog';
import {SubscriptionCardErrorDialog} from './SubscriptionCardErrorDialog';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveIcon from '@mui/icons-material/Remove';

interface SubscriptionCardProps {
  isActivePlan?: boolean;
  priceId: string;
  label: string;
  tierName: string;
  price: number;
  description: string;
  activeToursCount: number;
  uploadLimitCount: number;
  currencyCode: string;
  currencySymbol: string;
}

export function SubscriptionCard({
  isActivePlan,
  priceId,
  activeToursCount,
  uploadLimitCount,
  description,
  tierName,
  price,
  label,
  currencyCode,
  currencySymbol,
}: SubscriptionCardProps) {
  const {notify} = useSnackbarContext();
  const [paymentCheckout, {loading}] = useMutation<SubscriptionCardMutation>(
    graphql`
      mutation SubscriptionCardMutation($input: PaymentCheckoutInput!) {
        paymentCheckout(input: $input) {
          url
          message
          error
        }
      }
    `
  );
  const isFree = price === 0;

  const [message, setMessage] = useState('');
  const handleActivatePlan = async () => {
    const response = await paymentCheckout({
      variables: {
        input: {
          priceId: priceId,
          quantity: activeToursCount,
        },
      },
    });

    if (response.paymentCheckout.message !== '') {
      setMessage(response.paymentCheckout.message);
      manageDialog.onOpen();
      return;
    }
    if (response.paymentCheckout.error !== '') {
      setMessage(response.paymentCheckout.message);
      errorDialog.onOpen();
      return;
    }
    if (
      response.paymentCheckout.error != null &&
      response.paymentCheckout.error !== '' &&
      response.paymentCheckout.message !== ''
    ) {
      notify({message: response.paymentCheckout.message, variant: 'error'});
      return;
    }
    if (response.paymentCheckout.url != null) {
      // Redirect to Stripe's checkout page
      window.location.href = response.paymentCheckout.url;
    }
  };

  const theme = useTheme();

  const manageDialog = useTruvuDialog();
  const errorDialog = useTruvuDialog();

  const renderFeature = (
    feature: string,
    enabled: boolean,
    value?: string | number
  ) => (
    <>
      <Stack direction="row" justifyContent="space-between">
        <Typography color="text.primary">{feature}</Typography>
        {value !== undefined ? (
          <Typography color="text.secondary">{value}</Typography>
        ) : enabled ? (
          <CheckCircleIcon style={{color: theme.palette.primary.main}} />
        ) : (
          <RemoveIcon style={{color: theme.palette.grey[500]}} />
        )}
      </Stack>
      <Divider />
    </>
  );

  return (
    <>
      <Card
        elevation={0}
        sx={{
          flex: 1,
          minWidth: '250px',
          maxWidth: '450px',
          display: 'flex',
          flexDirection: 'column',
          p: 1.5,
          border: isActivePlan ? '2px solid #5374FC' : 'undefined',
        }}
      >
        <SubscriptionCardManageDialog {...manageDialog} message={message} />
        <SubscriptionCardErrorDialog {...errorDialog} message={message} />
        <TruvuButton
          size="default"
          variant={isActivePlan ? 'secondary' : 'primary'}
          sx={{alignSelf: 'flex-start', py: 1, px: 2.5}}
          disableRipple
          disableTouchRipple
          disableAdornments
        >
          {label}
        </TruvuButton>
        <Stack my={2} flex={1}>
          <Stack direction="row" alignItems="flex-end" mb={1} spacing={1}>
            <Typography variant="h1">
              {price > 0
                ? `${currencySymbol} ${price
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}`
                : 'Free'}{' '}
            </Typography>
            {price > 0 && <Typography>/ month</Typography>}
          </Stack>
          <Typography variant={'h6'}>{description}</Typography>
        </Stack>
        {renderFeature('Tours', true, activeToursCount)}
        {renderFeature('Views', true, 'unlimited')}
        {renderFeature('AI Room Tagging', true)}
        {renderFeature('Reels', true)}
        {renderFeature('Invite Team', true)}
        {renderFeature(
          'Tour Analytics',
          tierName === 'basic' || tierName === 'pro'
        )}
        {renderFeature(
          'Custom Company Branding',
          tierName === 'basic' || tierName === 'pro'
        )}
        {renderFeature('APIs & Webhooks', tierName === 'pro')}
        <TruvuButton
          sx={{mt: 1}}
          variant={isActivePlan ? 'secondary' : 'primary'}
          onClick={handleActivatePlan}
          disabled={isActivePlan}
          loading={loading}
        >
          {isActivePlan
            ? 'Current Plan'
            : isFree
            ? 'Start for free'
            : 'Activate Plan'}
        </TruvuButton>
      </Card>
    </>
  );
}

const Divider = styled('div')(({theme}) => ({
  height: '4px',
  display: 'flex',
  alignSelf: 'stretch',
  backgroundColor: theme.palette.divider,
  borderRadius: 8,
  marginTop: theme.spacing(0.5),
  marginBottom: theme.spacing(0.5),
}));
